import { useCallback, useMemo, useState } from "react";
import { SortingState } from "@tanstack/react-table";
import { keepPreviousData } from "@tanstack/react-query";

import { Stack } from "ui/stack";
import InboxHeader from "containers/admin/inbox/components/inbox-header/inbox-header";
import {
  DataGrid,
  TableContainer,
  TableFillHeightBody,
  TableLoader,
  shouldPinPrimaryColumnLeft,
} from "ui/data-grid";
import { Typography } from "ui/typography";

import { useCurrentUser } from "hooks/api/use-current-user";
import { useGetTouchpointVersionStatusesQuery } from "hooks/api/dictionary";
import { useInboxReviews } from "hooks/api/use-inbox-reviews";

import { useUserLayout, useDebouncedUpdateUserLayout } from "hooks/api/use-user-layout";

import { hasPermission } from "utilities/user";
import { getFilters } from "containers/admin/inbox/inbox.helper";
import { createInboxTableColumns } from "containers/admin/inbox/inbox-table-columns";
import {
  DEFAULT_INBOX_FILTERS_TO_APPLY,
  INBOX_FILTER_IDS,
} from "containers/admin/inbox/inbox.consts";
import { ColumnOption } from "types/table";

import { Permission } from "types/auth";
import { QuickFilterTypes, InboxFilter } from "types/inbox";
import { FiltersToApplyOnInbox } from "containers/admin/inbox/inbox.types";

type InboxPageProps = {
  lockedClientId?: string;
};

const TABLE_CONFIG_KEY = "recentCreatives";
const PRIMARY_COLUMN_IDS = ["touchpointName", "warningIcon"];

const hasAnyAppliedFilter = (filters: FiltersToApplyOnInbox): boolean =>
  Object.values(filters).some((value) => !!value?.length);

const InboxPage = ({ lockedClientId }: InboxPageProps) => {
  const currentUser = useCurrentUser();
  const isClientUser = hasPermission(currentUser, Permission.PERM_CLIENT_USER);

  const [filtersToApply, setFiltersToApply] = useState<FiltersToApplyOnInbox>(() => {
    if (isClientUser) {
      return {
        ...DEFAULT_INBOX_FILTERS_TO_APPLY,
        [INBOX_FILTER_IDS.quickFilters]: [QuickFilterTypes.toReview],
      };
    }
    return DEFAULT_INBOX_FILTERS_TO_APPLY;
  });

  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");
  const [sortingParams, setSortingParams] = useState<SortingState>([
    { id: "lastModifiedDate", desc: true },
  ]);

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const handleSearchTermChange = useCallback((searchTerm: string) => {
    setCurrentSearchTerm(searchTerm);
    setPageIndex(0);
  }, []);

  const handleFiltersChange = useCallback((next: FiltersToApplyOnInbox) => {
    setFiltersToApply(next);
    setPageIndex(0);
  }, []);

  const { data: touchpointVersionStatuses = [] } = useGetTouchpointVersionStatusesQuery();

  const { data: tableColumnsConfig = {} } = useUserLayout();
  const { toggleColumnVisibility } = useDebouncedUpdateUserLayout();

  const handleToggleColumn = (columnId: string) => {
    toggleColumnVisibility(TABLE_CONFIG_KEY, columnId);
  };

  const allColumns = useMemo(
    () => createInboxTableColumns({ touchpointVersionStatuses }),
    [touchpointVersionStatuses],
  );

  const columnOptions = useMemo<Array<ColumnOption>>(
    () =>
      allColumns
        .filter(
          (col): col is typeof col & { id: string; header: string } =>
            typeof col.header === "string" &&
            col.id !== undefined &&
            col.id !== "touchpointName" &&
            col.id !== "actions",
        )
        .map((col) => ({ id: col.id, label: col.header })),
    [allColumns],
  );

  const hiddenColumnIds = useMemo(
    () => new Set(tableColumnsConfig[TABLE_CONFIG_KEY]?.hidden ?? []),
    [tableColumnsConfig],
  );

  const columns = useMemo(
    () => allColumns.filter((col) => !hiddenColumnIds.has(col.id ?? "")),
    [allColumns, hiddenColumnIds],
  );

    const pinPrimaryColumnLeft = shouldPinPrimaryColumnLeft(columns, PRIMARY_COLUMN_IDS);

  const sortQueryParam = useMemo(
    () => sortingParams.map((param) => `${param.id},${param.desc ? "desc" : "asc"}`).join(","),
    [sortingParams],
  );

  // Build filters once per render; the request is scoped to the correct client:
  // - `lockedClientId` (Recent Creatives) overrides client scope explicitly.
  // - Client users are restricted to their own clients.
  const filters = useMemo<InboxFilter>(() => {
    const baseFilters: InboxFilter = getFilters(filtersToApply);

    if (lockedClientId) {
      baseFilters.clientId = [lockedClientId];
    } else if (isClientUser) {
      baseFilters.clientId = currentUser?.clients
        ?.map((client) => client.id)
        .filter((id): id is string => id !== undefined);
    }

    return { ...baseFilters, q: currentSearchTerm };
  }, [filtersToApply, lockedClientId, isClientUser, currentUser, currentSearchTerm]);

  const { data, isLoading, isFetching } = useInboxReviews(
    {
      page: pageIndex,
      size: pageSize,
      sort: sortQueryParam || undefined,
      filters,
    },
    { placeholderData: keepPreviousData },
  );

  const currentSearchResults = data?.items ?? [];
  const totalRecords = data?.totalElements ?? 0;
  const pagesAmount = data?.totalPages ?? 0;

  const handleAmountChange = ({ pageSize: nextPageSize }: { pageSize: number }) => {
    setPageSize(nextPageSize);
    setPageIndex(0);
  };

  const hasFiltersOrSearch = hasAnyAppliedFilter(filtersToApply) || !!currentSearchTerm;

  return (
    <TableContainer fillHeight>
      <InboxHeader
        setCurrentSearchTerm={handleSearchTermChange}
        isLoading={isFetching}
        filtersToApply={filtersToApply}
        setFiltersToApply={handleFiltersChange}
        tableColumnsConfig={tableColumnsConfig}
        columnOptions={columnOptions}
        onToggleColumn={handleToggleColumn}
      />

      <TableFillHeightBody>
        <TableLoader isLoading={isLoading} fullScreen />

        {currentSearchResults.length > 0 ? (
          <DataGrid
            columns={columns}
            data={currentSearchResults}
            isLoading={isFetching}
            manualSorting={false}
            showingRecordsIsOn={true}
            viewByAmountSelectIsOn={true}
            totalRecords={totalRecords}
            pageSize={pageSize}
            pagesAmount={pagesAmount}
            setAmount={handleAmountChange}
            pageIndex={pageIndex}
            setPageIndex={(pageIndex) => setPageIndex(pageIndex)}
            sortingParams={sortingParams}
            setSortingParams={setSortingParams}
            pinPrimaryColumnLeft={pinPrimaryColumnLeft}
            fillHeight
          />
        ) : (
          !isFetching && <EmptyTable hasFiltersOrSearch={hasFiltersOrSearch} />
        )}
      </TableFillHeightBody>
    </TableContainer>
  );
};

export default InboxPage;

interface EmptyTableProps {
  hasFiltersOrSearch: boolean;
}

const EmptyTable = ({ hasFiltersOrSearch }: EmptyTableProps) => {
  return (
    <Stack spacing="0.5rem" sx={{ alignItems: "center" }}>
      <Typography variant="h3" sx={{ color: "secondary.main", mt: 2 }}>
        {hasFiltersOrSearch ? "No results" : "No items"}
      </Typography>
      <Typography variant="body1">
        {hasFiltersOrSearch
          ? "Try changing your filters to view more items."
          : "You don't have access to any creatives. Check back later or reach out to Two Ocean."}
      </Typography>
    </Stack>
  );
};
